import { useCallback, useEffect, useState } from 'react';
import { Button, Callout, Card, PropertyList, PropertyListItem, Tag } from '@humanforce/mango-ui-react';
import { getPayAdvicePreparation, retryPayAdvicePreparation } from './payRunsHttpClient';
import type { PayAdvicePreparationSummaryDto, PayAdvicePreparationWorkerDto } from './payRunsApiTypes';

const tone = (status: PayAdvicePreparationWorkerDto['status']) => {
  if (status === 'prepared') return 'positive' as const;
  if (status === 'blocked' || status === 'failed') return 'critical' as const;
  return 'pending' as const;
};

const reason = (code?: string | null) => {
  switch (code) {
    case 'historical_person_unavailable': return 'Finalised person evidence is unavailable.';
    case 'historical_population_revision_unavailable': return 'Finalised worker population evidence is unavailable.';
    case 'historical_person_ambiguous': return 'Finalised worker identity needs review.';
    case 'historical_transaction_input_unavailable': return 'Finalised pay input evidence is unavailable.';
    case 'reconciliation_mismatch': return 'Finalised payroll totals need review.';
    case 'worker_evidence_unavailable': return 'Finalised worker evidence is unavailable.';
    case 'finalisation_evidence_inconsistent': return 'Finalised preparation evidence needs review.';
    case 'pay_advice_compliance_blocked': return 'Required Pay Advice content cannot be proven from finalised evidence.';
    case 'preparation_execution_failed': return 'Preparation could not complete. A manager can request recovery.';
    case 'manual_retry_requested': return 'Recovery requested; preparation is pending.';
    case 'clear': return 'Required preparation checks passed.';
    default: return code ? 'Preparation requires review.' : 'Preparation is in progress.';
  }
};

interface Props { payId: string; finalisationId: string }

/** Readiness only: no document is rendered, stored, issued, or delivered here. */
export const PayAdvicePreparationPanel = ({ payId, finalisationId }: Props) => {
  const [summary, setSummary] = useState<PayAdvicePreparationSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await getPayAdvicePreparation(payId, signal);
      if (!signal?.aborted && next.finalisationId === finalisationId) {
        setSummary(next);
        setError(null);
      }
    } catch {
      if (!signal?.aborted) setError('Pay Advice preparation status could not be loaded.');
    }
  }, [payId, finalisationId]);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  useEffect(() => {
    if (!summary || summary.pendingCount === 0 && !summary.hasAutomaticRetry) return;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh(controller.signal);
    }, 10000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [refresh, summary?.pendingCount, summary?.hasAutomaticRetry]);

  const retry = async () => {
    setRetrying(true);
    try {
      setSummary(await retryPayAdvicePreparation(payId));
      setError(null);
    } catch {
      setError('Recovery could not be requested. Refresh and try again.');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <section aria-label="Pay Advice preparation" data-testid="pay-advice-preparation">
      <Card header={<span>Pay Advice preparation</span>}>
        <div className="mgo-stack mgo-gap-m">
          <p className="mgo-text-s">Preparation checks finalised payroll evidence. No Pay Advice has been issued.</p>
          {error && <Callout tone="critical" title="Preparation status unavailable" action={<Button label="Retry loading" size="small" variant="secondary" onClick={() => void refresh()} />}>{error}</Callout>}
          {summary ? (
            <>
              <PropertyList>
                <PropertyListItem label="Overall status">{summary.overallStatus}</PropertyListItem>
                <PropertyListItem label="Prepared">{summary.preparedCount}</PropertyListItem>
                <PropertyListItem label="Blocked">{summary.blockedCount}</PropertyListItem>
                <PropertyListItem label="Failed">{summary.failedCount}</PropertyListItem>
                <PropertyListItem label="Pending">{summary.pendingCount}</PropertyListItem>
              </PropertyList>
              {summary.workers.length === 0 && <p className="mgo-text-s">{summary.zeroWorkerCompleted
                ? 'The finalised Pay Run has no workers; preparation is complete.'
                : summary.overallStatus === 'blocked' ? 'Finalised preparation evidence needs review.'
                : 'No worker result is available yet.'}</p>}
              {summary.workers.length > 0 && (
                <div className="mgo-stack mgo-gap-s" aria-label="Worker preparation results">
                  {summary.workers.map((worker) => (
                    <div key={worker.workEntityId} className="mgo-cluster mgo-gap-s" data-testid="pay-advice-worker-result">
                      <span>{worker.workEntityId}</span>
                      <Tag label={worker.status} tone={tone(worker.status)} />
                      <span className="mgo-text-s">{reason(worker.reasonCode)}</span>
                    </div>
                  ))}
                </div>
              )}
              {summary.canRetry && <Button label="Retry failed preparation" size="small" variant="secondary" loading={retrying} onClick={() => void retry()} />}
            </>
          ) : !error ? <p className="mgo-text-s">Loading preparation status…</p> : null}
        </div>
      </Card>
    </section>
  );
};
